import { BaseService } from '@common/http-layer/service/base.service'

import type { PaginationParams } from '@core/types'

import { sanitizeCourseTagIds } from '../utils/courses-filtering'

import type {
  AccessibleCourseGroup,
  AddCourseToGroupInput,
  CourseResponse,
  CreateCourseInput,
  GroupCourseTag,
  GroupCourseTagsResponse,
  GroupCoursesPaginatedResponse,
  GroupCoursesPaginationParams,
  ICourseProduct,
  IGroupCourseRef,
  LibraryCoursesProgressResponse,
  ReorderCoursesInput,
  UpdateGroupCourseInput,
  UserCoursesPaginatedResponse,
  UserCoursesPaginationParams,
} from '../types'

/**
 * Max product ids the library progress endpoint accepts per request (see backend spec).
 * The service caps the payload defensively so a large page limit or an accidental over-fill
 * never trips a 400 at the wire layer.
 */
export const LIBRARY_COURSES_PROGRESS_MAX_BATCH = 50

/**
 * Courses service - handles all course-related API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /{locationId}/groups/{groupId}
 */
export class CoursesService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '', // We'll build the path dynamically with locationId and groupId
    })
  }

  /**
   * Get location and group-aware base path
   */
  private getBasePath(locationId: string, groupId: string, withoutCourses: boolean = false): string {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    return `/${locationId}/groups/${groupId}/${withoutCourses ? '' : 'courses'}`
  }

  /**
   * Get courses for group admins - legacy v1 response (full list).
   * Prefer `fetchGroupCoursesPaginated` for the courses page; keep this for callers that need a full list
   * (e.g. share-as-post lookups, admin dropdowns).
   */
  async fetchGroupCourses(groupId: string, locationId: string, query?: PaginationParams): Promise<ICourseProduct[]> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<ICourseProduct[]>(groupPath, {
      params: query as Record<string, string | number | boolean> | undefined,
      groupId,
      locationId,
    })
    const courses = data || []
    // Add locationId to each course
    return courses.map((course: ICourseProduct) => ({ ...course, locationId }))
  }

  /**
   * Get the pool of location-group courses that can be added to this group as a lightweight
   * reference list.
   * GET /{locationId}/groups/{groupId}/candidate-courses
   *
   * Admin-only endpoint that returns the full (non-paginated) set of addable courses — the
   * backend already excludes courses that are already attached to the group. Drives the Add
   * Course dropdown; the paginated v2 lists can't be used here because they only surface
   * loaded pages.
   */
  async fetchAddableCourses(groupId: string, locationId: string): Promise<IGroupCourseRef[]> {
    const groupPath = `/${locationId}/groups/${groupId}/candidate-courses`
    const { data } = await this.get<IGroupCourseRef[] | { courses: IGroupCourseRef[] }>(groupPath, {
      groupId,
      locationId,
    })
    const courses = Array.isArray(data) ? data : data?.courses || []
    return courses
  }

  /**
   * Get paginated group courses via the v2 contract (admin/portal group view).
   * GET /{locationId}/groups/{groupId}/courses?version=2&pageNo={pageNo}&limit={limit}
   *
   * Unlike the user-courses endpoint, this endpoint does not apply learner access post-filtering,
   * so `total` / `totalPages` accurately reflect the returned page counts.
   *
   * Accepts the same filter/sort contract as `fetchUserCoursesPaginated` so guest / logged-out
   * viewers get the same filtering + sorting behavior as logged-in learners.
   */
  async fetchGroupCoursesPaginated(
    groupId: string,
    locationId: string,
    params: GroupCoursesPaginationParams
  ): Promise<GroupCoursesPaginatedResponse> {
    const groupPath = this.getBasePath(locationId, groupId)

    // BaseService's `params` typing doesn't natively serialize arrays; the platform convention
    // (see MembersService.memberRole, LiveSessionService.eventIds) is a comma-separated string.
    // Mirrors `fetchUserCoursesPaginated` so both endpoints share one filter/sort contract.
    const queryParams: Record<string, string | number | boolean> = {
      // `version=2` is REQUIRED to opt into the paginated + filterable contract; without it the
      // backend silently returns the legacy v1 array. Do NOT parameterize this — it must be `2`.
      version: 2,
      pageNo: params.pageNo,
      limit: params.limit,
    }
    const safeTagIds = sanitizeCourseTagIds(params.tagIds)
    if (safeTagIds.length > 0) {
      queryParams.tagIds = safeTagIds.join(',')
    }
    if (params.sortBy) queryParams.sortBy = params.sortBy
    if (params.sort) queryParams.sort = params.sort

    const { data } = await this.get<GroupCoursesPaginatedResponse>(groupPath, {
      params: queryParams,
      groupId,
      locationId,
    })
    const courses = (data?.courses || []).map((course: ICourseProduct) => ({ ...course, locationId }))
    return {
      courses,
      pageNo: data?.pageNo ?? params.pageNo,
      limit: data?.limit ?? params.limit,
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 0,
    }
  }

  /**
   * Get courses for regular users (with access control) - legacy v1 response (full list, includes progress).
   * Prefer `fetchUserCoursesPaginated` for the courses page; keep this for callers that need a full list
   * (e.g. course selection dropdowns, purchase-page lookup).
   */
  async fetchUserCourses(groupId: string, locationId: string, query?: PaginationParams): Promise<ICourseProduct[]> {
    const groupPath = this.getBasePath(locationId, groupId, true)
    const { data } = await this.get<ICourseProduct[]>(`${groupPath}user/courses`, {
      params: query as Record<string, string | number | boolean> | undefined,
      groupId,
      locationId,
    })
    const courses = data || []
    // Add locationId to each course
    return courses.map((course: ICourseProduct) => ({ ...course, locationId }))
  }

  /**
   * Get paginated user courses via the v2 contract.
   * GET /{locationId}/groups/{groupId}/user/courses?version=2&pageNo={pageNo}&limit={limit}
   *
   * The v2 endpoint post-filters by private-channel access, so `courses.length` may be less than `limit`
   * on any given page; `total`/`totalPages` reflect the pre-filter count and drive pagination.
   */
  async fetchUserCoursesPaginated(
    groupId: string,
    locationId: string,
    params: UserCoursesPaginationParams
  ): Promise<UserCoursesPaginatedResponse> {
    const groupPath = this.getBasePath(locationId, groupId, true)

    // BaseService's `params` typing doesn't natively serialize arrays; the platform convention
    // (see MembersService.memberRole, LiveSessionService.eventIds) is a comma-separated string.
    // The v2 backend accepts BOTH repeated `?tagIds=...&tagIds=...` and single `?tagIds=a,b,c`;
    // we ship the comma form to stay consistent with the rest of the codebase.
    const queryParams: Record<string, string | number | boolean> = {
      // `version=2` is REQUIRED to opt into the paginated + filterable contract; without it the
      // backend silently returns the legacy v1 array. Do NOT parameterize this — it must be `2`.
      version: 2,
      pageNo: params.pageNo,
      limit: params.limit,
    }
    const safeTagIds = sanitizeCourseTagIds(params.tagIds)
    if (safeTagIds.length > 0) {
      queryParams.tagIds = safeTagIds.join(',')
    }
    if (params.sortBy) queryParams.sortBy = params.sortBy
    if (params.sort) queryParams.sort = params.sort

    const { data } = await this.get<UserCoursesPaginatedResponse>(`${groupPath}user/courses`, {
      params: queryParams,
      groupId,
      locationId,
    })
    const courses = (data?.courses || []).map((course: ICourseProduct) => ({ ...course, locationId }))
    return {
      courses,
      pageNo: data?.pageNo ?? params.pageNo,
      limit: data?.limit ?? params.limit,
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 0,
    }
  }

  /**
   * Fetch the deduped catalog of tags in use by any course in the group.
   * Drives the filter panel (grouped by `category`).
   *
   * GET /{locationId}/groups/{groupId}/course-tags
   *
   * Backend caches for 3 hours and auto-invalidates on any course add/update/remove in the group,
   * so the client does not need to bust anything after mutations.
   */
  async fetchCourseTags(groupId: string, locationId: string): Promise<GroupCourseTagsResponse> {
    const groupPath = this.getBasePath(locationId, groupId, true)
    // The backend currently returns `{ tags: GroupCourseTag[] }`, but historical builds and
    // some staging deployments have shipped a bare `GroupCourseTag[]` array. Normalize both
    // shapes here so a shape drift on the API side never silently empties the filter panel.
    const { data } = await this.get<GroupCourseTagsResponse | GroupCourseTag[]>(`${groupPath}/course-tags`, {
      groupId,
      locationId,
    })
    const tags = Array.isArray(data) ? data : (data?.tags ?? [])
    return { tags }
  }

  /**
   * Get a single course by ID
   */
  async getCourseById(courseId: string, groupId: string, locationId: string): Promise<ICourseProduct> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<ICourseProduct>(`${groupPath}/${courseId}`, {
      groupId,
      locationId,
    })
    // Add locationId to the course
    return data
  }

  /**
   * Create a new course
   */
  async createCourse(data: CreateCourseInput, groupId: string, locationId: string): Promise<ICourseProduct> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data: responseData } = await this.post<CourseResponse>(groupPath, data, {
      groupId,
      locationId,
    })
    // Add locationId to the course
    return { ...responseData.course }
  }

  /**
   * Update an existing course
   */
  async updateCourse(courseId: string, data: UpdateGroupCourseInput, groupId: string, locationId: string): Promise<ICourseProduct> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data: responseData } = await this.patch<CourseResponse>(`${groupPath}/${courseId}`, data, {
      groupId,
      locationId,
    })
    // Add locationId to the course
    return { ...responseData.course }
  }

  /**
   * Delete a course
   */
  async deleteCourse(courseId: string, groupId: string, locationId: string): Promise<void> {
    const groupPath = this.getBasePath(locationId, groupId)
    await this.delete(`${groupPath}/${courseId}`, {
      groupId,
      locationId,
    })
  }

  /**
   * Reorder courses within a group
   */
  async reorderCourses(data: ReorderCoursesInput): Promise<{ success: boolean; message?: string }> {
    const groupPath = this.getBasePath(data.locationId, data.groupId, true)
    const basePath = `${groupPath}rearrange/courses/${data.productId}`

    // Build query params
    const params: Record<string, string> = {}
    if (data.previousProductId) {
      params.previousProductId = data.previousProductId
    }
    if (data.nextProductId) {
      params.nextProductId = data.nextProductId
    }

    const { data: responseData } = await this.patch<{ success: boolean; message?: string }>(
      basePath,
      undefined, // No body for this endpoint
      {
        params,
        groupId: data.groupId,
        locationId: data.locationId,
      }
    )
    return responseData
  }
  /**
   * Get course progress for a user
   */
  async getCourseProgress(courseId: string, groupId: string, locationId: string): Promise<{ progress: number }> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<{ progress: number }>(`${groupPath}/${courseId}/progress`, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Update course progress for a user
   */
  async updateCourseProgress(courseId: string, progress: number, groupId: string, locationId: string): Promise<{ success: boolean }> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.put<{ success: boolean }>(
      `${groupPath}/${courseId}/progress`,
      { progress },
      {
        groupId,
        locationId,
      }
    )
    return data
  }

  /**
   * Get course suggestions for mentions (search/filter courses)
   */
  async getCoursesSuggestions(groupId: string, locationId: string, searchQuery?: string): Promise<ICourseProduct[]> {
    const basePath = `/${locationId}/groups/${groupId}/user/courses/search`
    const { data } = await this.get<ICourseProduct[]>(basePath, {
      params: { search: searchQuery || '' },
      groupId,
      locationId,
    })
    return data || []
  }

  /**
   * Get all location-based courses (courses available in the location that can be added to groups)
   * This fetches from the membership/courses product, not group-specific courses
   */
  async fetchAllLocationCourses(locationId: string, paginationParams?: PaginationParams): Promise<ICourseProduct[]> {
    if (!locationId) throw new Error('Location ID is required')

    const { data } = await this.get<{ courses: ICourseProduct[] } | ICourseProduct[]>(`/${locationId}/courses`, {
      params: paginationParams as Record<string, string | number | boolean> | undefined,
      locationId,
    })

    // Handle both response formats: { courses: [...] } or [...]
    const courses = Array.isArray(data) ? data : data?.courses || []

    // Add locationId and sort alphabetically by title
    return courses.map((course: ICourseProduct) => ({ ...course, locationId })).sort((a, b) => a.title.localeCompare(b.title))
  }

  /**
   * Get groups where the current user can share this course.
   * GET /{locationId}/courses/{productId}/accessible-groups
   */
  async fetchAccessibleGroupsForCourse(locationId: string, productId: string): Promise<AccessibleCourseGroup[]> {
    if (!locationId) throw new Error('Location ID is required')
    if (!productId) throw new Error('Product ID is required')

    const { data } = await this.get<AccessibleCourseGroup[]>(`/${locationId}/courses/${productId}/accessible-groups`, {
      locationId,
      productId,
    })

    return data || []
  }

  /**
   * Add a course to a group
   * POST /{locationId}/groups/{groupId}/courses
   */
  async addCourseToGroup(groupId: string, locationId: string, input: AddCourseToGroupInput): Promise<ICourseProduct> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.post<ICourseProduct>(groupPath, input, {
      groupId,
      locationId,
    })
    return { ...data, locationId }
  }

  /**
   * Fetch per-course progress for the authenticated caller for a batch of product ids.
   * POST /{locationId}/groups/{groupId}/user/courses/library/progress
   *
   * Body: `{ productIds: string[] }` (1–50 uuids). Response: `Record<productId, { progress, totalPosts, completedPosts }>`.
   *
   * The v2 courses list endpoint stopped inlining progress because that made it slow.
   * Callers should render the list first, then call this per page to populate progress lazily.
   * Empty payloads short-circuit to `{}` so callers can safely invoke this without pre-guarding.
   *
   * `skipCache` (testing-only flag on the backend, per API spec — will be removed once the
   * endpoint stabilises) bypasses the upstream progress cache. Backend only honors it when
   * a single productId is sent, so it's a no-op for our multi-course batches. Kept plumbed
   * through so we can test with it during rollout.
   */
  async fetchLibraryCoursesProgress(
    groupId: string,
    locationId: string,
    productIds: string[],
    options?: { skipCache?: boolean }
  ): Promise<LibraryCoursesProgressResponse> {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    // Filter empty/undefined ids and cap to the backend's max batch size so an oversized
    // page or a stray placeholder id never trips a 400 at the wire layer.
    const cleaned = Array.from(new Set((productIds || []).filter((id): id is string => !!id))).slice(0, LIBRARY_COURSES_PROGRESS_MAX_BATCH)
    if (cleaned.length === 0) return {}

    const path = `/${locationId}/groups/${groupId}/user/courses/library/progress`
    const requestConfig: {
      groupId: string
      locationId: string
      params?: Record<string, boolean>
    } = { groupId, locationId }
    if (options?.skipCache) {
      // Only serialize the query param when explicitly asked — omitting it entirely (rather
      // than sending `skipCache=false`) matches the wire convention used across other v2 endpoints.
      requestConfig.params = { skipCache: true }
    }

    const { data } = await this.post<LibraryCoursesProgressResponse>(path, { productIds: cleaned }, requestConfig)
    return data || {}
  }

  /**
   * Purchase a course for the current user within a community group.
   * POST /{locationId}/groups/{groupId}/user/courses/purchase
   */
  async purchaseCourse(groupId: string, locationId: string, offerId: string): Promise<unknown> {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')
    const { data } = await this.post(`/${locationId}/groups/${groupId}/user/courses/purchase`, { offerId }, { groupId, locationId })
    return data
  }
}
